// Libraries
import format from 'date-fns-tz/format';
import { addMonths, differenceInCalendarMonths, addQuarters, addYears, isBefore } from 'date-fns';

/**
 * Builds tick values and tick formats for a given date range.
 *
 * @function
 *
 * @param startDate - The start date of the range.
 * @param endDate - The end date of the range.
 * @returns An object containing tick values and tick formats.
 */
export const buildFoulingTickValues = ([startDate, endDate]: [Date, Date]) => {
  const tickValues = [];
  const tickFormat = [];
  const monthsOfDifference = differenceInCalendarMonths(endDate, startDate);
  const oneYear = monthsOfDifference <= 12;
  const lessThanSixty = monthsOfDifference <= 60;
  const moreThanSixty = monthsOfDifference > 60;
  let intervals;

  if (oneYear) {
    intervals = 1;
  } else if (lessThanSixty) {
    intervals = 3;
  } else {
    intervals = 12;
  }

  const maxSteps = monthsOfDifference / intervals;

  for (let i = 0; i <= maxSteps; i++) {
    let date;
    if (oneYear) {
      date = addMonths(startDate, i);
    } else if (lessThanSixty) {
      date = addQuarters(startDate, i);
    } else {
      date = addYears(startDate, i);
    }

    if (isBefore(endDate, date)) break;

    if (i == 0 || i == Math.ceil(maxSteps) - 1){
      // To avoid overlap when adding start and end date
      continue;
    }

    tickValues.push(date);

    if (moreThanSixty) {
      tickFormat.push(format(date, 'yyyy'));
    } else if (i % 12 === 0 || date.getMonth() === 0) {
      tickFormat.push(format(date, 'MMM yyyy').split(' ').join('\n'));
    } else {
      tickFormat.push(format(date, 'MMM'));
    }
  }

  // Those are added to have an initial and final date format on each tick.
  [startDate, endDate].forEach((date) => {
    tickValues.push(date);
    tickFormat.push(format(date, 'MMM yyyy').split(' ').join('\n'));
  });

  return { tickValues, tickFormat };
};
